<template>
  <div class="exam">
    <div class="exam-flex">
      <div class="box left">
        <div class="exam-title">
          <div class="one-line">{{ examData?.examinationName }}</div>
          <div class="two-line">
            <span
              >总分：{{ examData?.paperScore }}分 共计
              {{ examData?.questions?.length }}道题</span
            >
          </div>
          <div class="clock">
            <div class="clock-time">
              剩余时长: <span> {{ countdown }} </span>分钟
            </div>
            <div class="clock-text">考试结束，将自动交卷</div>
          </div>
          <div class="exam-tip">
            <div>考试</div>
            <div>试卷</div>
          </div>
        </div>
        <div class="exam-content">
          <!-- 题目类型循环 -->
          <!-- 多选 -->
          <div class="exam-option">
            <TheoryQs
              ref="theoryQsRef"
              @success="answerChange"
              v-if="examData?.examType == 'theory'"
            />
            <practicalQs
              ref="practicalQsRef"
              @success="answerChange"
              v-if="examData?.examType == 'practical'"
            />
          </div>
        </div>
        <div class="exam-page">
          ({{ answerType?.label }}) 第{{ answerType.index }}题 共{{
            answerType?.count
          }}题
        </div>
      </div>
      <div class="box right">
        <div class="right-header">考生信息</div>
        <div class="right-user">
          <div>考生姓名：{{ userInfo?.memberName }}</div>
          <div>准考证号：{{ userInfo?.memberCode }}</div>
          <div>身份证号：{{ userInfo?.idCard }}</div>
        </div>
        <div class="right-header">试卷信息</div>
        <div class="right-scroll">
          <el-scrollbar>
            <div
              class="right-box"
              v-for="(i, k) in questionTypeOptions"
              :key="k"
            >
              <div class="ques-title">{{ quesIndex[k] }}、{{ i.label }}</div>
              <div
                v-for="(item, index) in i.questions"
                @click="handleCurrentChange(item, index, i)"
                :key="index"
                :class="[
                  'right-btn',
                  answerIndex == item.index ? 'is-current' : '',
                  hasValidValue(item.questionsId) ? 'is-answer' : '',
                ]"
              >
                {{ index + 1 }}
              </div>
            </div>
          </el-scrollbar>
        </div>
        <el-tooltip
          effect="dark"
          :content="`考试结束前${appStore.getExamPaper?.advanceSubmissionTime}分钟方可交卷`"
          placement="top"
        >
          <div :class="['btn']" style="margin: 20px auto" @click="submit">
            交卷
          </div>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>
<script setup>
import {
  ref,
  onMounted,
  computed,
  onUnmounted,
  watch,
  getCurrentInstance,
} from "vue";
import { dateDiff } from "../../utils";
import { ElMessage } from "element-plus";
import { useRouter } from "vue-router";
import { examPaperGet, examAnswerGet, examAnswerSet } from "@/api/index.js";
import {
  requestFullscreen,
  isFullscreen,
  enableExamGuard,
  disableExamGuard,
  exitFullscreenSafe,
} from "@/utils/examGuard";
import { useAppStore } from "../../store";
import TheoryQs from "./TheoryQs.vue";
import {
  theoryQuestions,
  practicalQuestions,
  quesIndex,
} from "@/utils/dict.js";
import PracticalQs from "./PracticalQs.vue";
const appStore = useAppStore();
const router = useRouter(); // 路由对象
const { proxy } = getCurrentInstance();

// 答案数据
const answerData = ref({});
// 当前题目索引
const answerIndex = ref(0);
const answerType = ref("");
// 右侧选择分页
const questionTypeOptions = ref({});
// 试卷信息
const examData = computed(() => {
  return appStore.getExamPaper;
});

/** 试卷字段 antiCheatEnabled === false 时关闭；未下发时默认开启 */
const antiCheatEnabled = computed(
  () => examData.value?.antiCheatEnabled !== false,
);

const timeNow = computed(() => {
  return appStore.getServerTime;
});
// 考生信息
const userInfo = computed(() => {
  return JSON.parse(localStorage.getItem("user_info")) || {};
});
const hasValidValue = (key) => {
  const obj = answerData.value;
  if (!obj || typeof obj !== "object") return false;
  if (!Object.prototype.hasOwnProperty.call(obj, key)) return false;

  const val = obj[key];

  // null / undefined
  if (val === null || val === undefined) return false;

  // 字符串
  if (typeof val === "string") return val.trim().length > 0;

  // 数组
  if (Array.isArray(val)) return val.length > 0;

  // 对象
  if (typeof val === "object") return Object.keys(val).length > 0;

  // 其他类型（number / boolean / function）
  return true;
};

// 倒计时
const countdown = computed(() => {
  return timeNow.value && appStore.getExamPaper?.endTime
    ? dateDiff(timeNow.value, appStore.getExamPaper.endTime)
    : "";
});
// 自动交卷
const autoSubmit = async () => {
  await examAnswerSet({
    ...userInfo.value,
    answer: answerData.value,
    isDone: true,
  });
  await disableExamGuard();
  await exitFullscreenSafe();
  appStore.CLEAR_CACHE();
  router.push("/finish");
};
watch(
  () => timeNow.value,
  async (val) => {
    if (val >= new Date(examData.value?.endTime).getTime()) {
      // ✅ 全部答完，执行交卷
      autoSubmit();
    }
  },
  { immediate: true },
);
// 答题触发
const answerChange = async (params) => {
  answerData.value[String(params.question.questionsId)] = params.answer;
  const res = await examAnswerSet({
    ...userInfo.value,
    answer: answerData.value,
  });
  if (res?.status) {
    ElMessage.warning(res.msg);
    appStore.CLEAR_CACHE();
    router.push("/finish");
  }
};
// 切换题目和交卷的时候触发答题接口
const submit = async () => {
  // 判断是否到交卷时间
  const endTimestamp = new Date(examData.value?.endTime).getTime();
  const limitTime =
    endTimestamp - examData.value?.advanceSubmissionTime * 60 * 1000;
  if (timeNow.value < limitTime) {
    ElMessage.warning("未到交卷时间！");
    return;
  }
  for (let i = 0; i < questionTypeOptions.value.length; i++) {
    for (let j = 0; j < questionTypeOptions.value[i].questions.length; j++) {
      const q = questionTypeOptions.value[i].questions[j];
      const key = String(q.questionsId);
      const value = answerData.value[key];

      // 未答
      if (!value || !String(value).trim()) {
        ElMessage.warning(
          `${questionTypeOptions.value[i].label}第 ${
            j + 1
          } 题尚未作答，请完成后再交卷！`,
        );
        return;
      }
      // 多选题防御：", ,"
      if (!value) {
        ElMessage.warning(
          `${questionTypeOptions.value[i].label}第 ${
            j + 1
          } 题尚未作答，请完成后再交卷！`,
        );
        return;
      }
    }
  }
  // ✅ 全部答完，执行交卷
  await examAnswerSet({
    ...userInfo.value,
    answer: answerData.value,
    isDone: true,
  });
  await disableExamGuard();
  await exitFullscreenSafe();
  appStore.CLEAR_CACHE();
  router.push("/finish");
};
const handleCurrentChange = async (ques, index, i, isSumbit = true) => {
  if (isSumbit) {
    if (answerIndex.value == ques.index) return;
  }
  // if (isSumbit) {
  //   await setAnswer();
  // }
  answerIndex.value = ques.index;
  answerType.value = {
    value: i.value,
    label: i.label,
    index: index + 1,
    count: i.questions.length,
  };
  let questionData = examData.value.questions.find(
    (item) => item.questionsId == ques.questionsId,
  );
  const questionAnswer = answerData.value?.[String(ques.questionsId)];
  questionData.index = index;
  questionData.questionTypeName = i.label;
  if (userInfo.value?.examType == "theory") {
    proxy.$refs.theoryQsRef.open(questionData, questionAnswer);
  } else {
    proxy.$refs.practicalQsRef.open(questionData, questionAnswer);
  }
};
// 初始化题目分类
const topicClassification = () => {
  const options =
    userInfo.value.examType === "practical"
      ? practicalQuestions
      : theoryQuestions;
  // 1️⃣ 深拷贝 options，防止污染原数据
  const result = options.map((opt) => ({
    ...opt,
    questions: [],
  }));
  // 2️⃣ 遍历 data，按题型收集 index
  examData.value.questions.forEach((item, index) => {
    const type = Number(item.questionType);
    const target = result.find((opt) => opt.value === type);
    if (target) {
      target.questions.push({ index, questionsId: item.questionsId });
    }
  });
  // 3️⃣ 过滤掉 questions 为空的题型
  questionTypeOptions.value = result.filter((opt) => opt.questions.length > 0);
  // 默认选择第一题;
  handleCurrentChange(
    questionTypeOptions.value[0].questions[0],
    0,
    questionTypeOptions.value[0],
    false,
  );
};

// create
onMounted(async () => {
  if (!appStore.getExamPaper) {
    // 判断没有试卷缓存 获取试卷
    const res = await examPaperGet(
      JSON.parse(localStorage.getItem("user_info")),
    );
    const data = res.data;
    appStore.SET_PAPER(data);
  }
  // 获取答案数据
  const answerRes = await examAnswerGet(
    JSON.parse(localStorage.getItem("user_info")),
  );
  answerData.value = answerRes.data;
  topicClassification();
  if (!isFullscreen()) {
    await requestFullscreen();
  }
  if (antiCheatEnabled.value) {
    await enableExamGuard();
  }
});

onUnmounted(() => {
  disableExamGuard();
});
</script>
<style lang="less" scoped>
.exam {
  height: calc(100% - 60px);
  min-width: 1400px;
  padding: 30px;
  background-color: #f6f9ffff;

  .exam-flex {
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
  }
  .box {
    background-color: #fff;
    border-radius: 20px;
    box-shadow: 0 0 20px 2px #bfd2ef66;
    padding: 30px;
  }
  .left {
    flex: 1;
    overflow-y: auto;
    background-image: url("@/assets/images/exam/exam-bg.png");
    background-size: 100%;
    background-position: 0 0;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    height: calc(100% - 60px);
    .exam-title {
      position: relative;
      margin: 40px 0;
      color: #475775ff;
      position: relative;
      .one-line {
        text-align: center;
        font-size: 24px;
        font-weight: bold;
      }
      .two-line {
        background-color: #e7efffff;
        border-radius: 12px;
        line-height: 24px;
        width: max-content;
        margin: 0 auto;
        padding: 0 18px;
        font-size: 12px;
        margin-top: 20px;
      }
      .clock {
        position: absolute;
        right: 0;
        top: 0;
        background-color: #f7f8faff;
        border-radius: 10px;
        padding: 20px 24px;
        text-align: right;
        font-size: 16px;

        .clock-time {
          font-weight: bold;
          margin-bottom: 12px;
          color: #475975ff;
          > span {
            color: #ff9e44ff;
          }
        }
        .clock-text {
          font-size: 12px;
          color: #9ca0a2ff;
        }
      }
      .exam-tip {
        width: 60px;
        height: 70px;
        position: absolute;
        left: -30px;
        top: -30px;
        background-image: url("@/assets/images/exam/exam-tip.png");
        background-size: 100% 100%;
        color: #fff;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-size: 16px;
      }
    }

    .exam-content {
      &::before {
        content: "";
        position: absolute;
        top: -7px;
        left: 0;
        width: 100%;
        background-color: #aab8d2ff;
        height: 3px;
      }

      flex: 1;
      margin: 10px 100px 20px;
      background-image: url("@/assets/images/exam/ques-bg.png");
      background-color: #fff;
      background-repeat: repeat;
      position: relative;
      box-shadow: 0 0 10px 2px #bfd2ef66;

      .exam-option {
        padding: 60px 80px 30px;
        height: calc(100% - 90px);
      }

      .footer {
        position: absolute;
        bottom: 40px;
        right: 40px;
        display: flex;
        justify-content: flex-end;
        .btn {
          text-align: center;
          width: 90px;
          height: 36px;
          line-height: 36px;
          border-radius: 5px;
          color: #fff;
          background-color: #1c68ffff;
          border: 1px solid #1c68ffff;
          margin-left: 20px;
          font-size: 14px;
          cursor: pointer;
        }
        .is-disabled {
          border-color: #e4e7eeff;
          background-color: #fff;
          color: #808aa4ff;
        }
      }
    }
    .exam-page {
      text-align: center;
      color: #475775ff;
      margin-bottom: 40px;
    }
  }
  .right {
    width: 280px;
    margin-left: 30px;
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .right-header {
      position: relative;
      margin-left: 16px;
      height: 16px;
      line-height: 16px;
      color: #475056ff;
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 32px;
      &::before {
        content: "";
        position: absolute;
        width: 4px;
        height: 16px;
        left: -16px;
        top: 0;
        background-color: #1c68ffff;
      }
    }
    .right-user {
      background-color: #f6f9ff;
      border-radius: 10px;
      padding: 12px 16px;
      line-height: 32px;
      color: #1c77ff;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .right-scroll {
      flex: 1;
      height: calc(100% - 300px);
    }
    .right-box {
      .ques-title {
        width: 100%;
        margin-bottom: 8px;
        color: #475056ff;
        font-size: 16px;
        margin: 0 5px 10px;
        font-weight: bold;
      }
      display: flex;
      flex-wrap: wrap;
      .right-btn {
        width: 44px;
        height: 30px;
        line-height: 30px;
        text-align: center;
        border: 1px solid #dae5edff;
        border-radius: 4px;
        margin: 0 5px 10px;
        cursor: pointer;
      }

      .is-answer {
        background-color: #1c77ffff;
        color: #fff;
        border-color: #1c77ffff;
      }
      .is-current {
        border-color: #1c77ffff;
        background-color: #e8f3ffff;
        color: #1c77ffff;
      }
    }
    .btn {
      text-align: center;
      width: 90px;
      height: 36px;
      line-height: 36px;
      border-radius: 5px;
      color: #fff;
      background-color: #1c68ffff;
      border: 1px solid #1c68ffff;
      width: 90px;
      cursor: pointer;
    }
    .is-disable {
      border-color: #e4e7eeff;
      background-color: #e4e7eeff;
      color: #808aa4ff;
      cursor: not-allowed;
    }
  }
}
</style>
